import { ImageUp, Layers, MessagesSquare, ShieldCheck, SlidersHorizontal, Copy } from "lucide-react";

const STEPS = [
  { n: "01", icon: ImageUp, title: "Upload or paste", desc: "Add one or more screenshots, paste the chat, or just type their latest message. Fix anything the AI misread." },
  { n: "02", icon: SlidersHorizontal, title: "Set the vibe", desc: "Pick a language, one of five flirting levels and one of seven personalities." },
  { n: "03", icon: Copy, title: "Copy & send yourself", desc: "Get three distinct replies with a short reason for each. Tweak, regenerate or copy." },
];

export const HowItWorks = () => (
  <section id="how" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
    <div className="max-w-2xl mb-14 space-y-3">
      <div className="eyebrow">How it works</div>
      <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Three steps, zero awkward pauses.</h2>
    </div>
    <div className="grid md:grid-cols-3 gap-5">
      {STEPS.map(({ n, icon: Icon, title, desc }) => (
        <div key={n} data-testid={`how-step-${n}`} className="panel p-7 space-y-4 hover:-translate-y-1 transition-transform duration-300">
          <div className="flex items-center justify-between">
            <span className="font-display text-5xl font-extrabold text-primary/20">{n}</span>
            <Icon className="h-6 w-6 text-primary" />
          </div>
          <h3 className="text-xl font-semibold">{title}</h3>
          <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
        </div>
      ))}
    </div>
  </section>
);

const FEATURES = [
  { icon: ImageUp, tag: "Vision AI", title: "Screenshot analysis", desc: "Reads chat bubbles from your screenshots, keeps the order, skips names, timestamps and app buttons, and lets you correct everything before it's used.", span: "md:col-span-2" },
  { icon: MessagesSquare, tag: "Context", title: "Contextual replies", desc: "Replies answer their latest message using the recent conversation — without inventing facts or assuming feelings.", span: "" },
  { icon: Layers, tag: "Styles", title: "7 personalities × 5 levels", desc: "From Calm & Natural to Teasing & Playful, Friendly to Bold. Respectful and non-explicit by default.", span: "" },
  { icon: ShieldCheck, tag: "Privacy", title: "Privacy by design", desc: "Screenshots are processed in memory and never saved. Transcripts are kept only as long as you choose — or not at all — and one tap clears them.", span: "md:col-span-2" },
];

export const Features = () => (
  <section id="features" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
    <div className="max-w-2xl mb-12 space-y-3">
      <div className="eyebrow">Features</div>
      <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Built for real conversations.</h2>
    </div>
    <div className="grid md:grid-cols-3 gap-5">
      {FEATURES.map(({ icon: Icon, tag, title, desc, span }) => (
        <div key={title} data-testid={`feature-card-${tag.toLowerCase()}`} className={`panel group p-8 relative overflow-hidden ${span}`}>
          <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full brand-gradient opacity-0 group-hover:opacity-20 blur-2xl transition-opacity duration-500" />
          <div className="h-11 w-11 rounded-2xl bg-secondary flex items-center justify-center mb-6"><Icon className="h-5 w-5 text-primary" /></div>
          <div className="text-[11px] uppercase tracking-widest font-semibold text-accent mb-2">{tag}</div>
          <h3 className="text-xl font-semibold mb-2">{title}</h3>
          <p className="text-sm text-muted-foreground leading-relaxed max-w-lg">{desc}</p>
        </div>
      ))}
    </div>
  </section>
);
